import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { isqUnselectOption } from "@learningblocks/simulations/shared-components/lib/Questions/ImageSelectionQuestionRedux/store/actions";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { FormattedMessage } from "@learningblocks/shared-components/lib/FormattedMessage";
import { GridItem } from "@learningblocks/interactives/interactive-infographic-components/lib/GridItem";
import { AnimationEntity } from "@learningblocks/interactives/interactive-infographic-components/lib/AnimationEntity";
import { ImageEntity } from "@learningblocks/interactives/interactive-infographic-components/lib/ImageEntity";
import { NotebookEntity } from "@learningblocks/interactives/interactive-infographic-components/lib/NotebookEntity";
import { Hotspot } from "@learningblocks/interactives/interactive-infographic-components/lib/Hotspot";
import {
  willTriggerAnimation,
  getAnimationAltTexts,
  isNavigableContentHotspot,
  sectionRevealTrigger,
  createClickHandler,
} from "@learningblocks/interactives/interactive-infographic-components/lib/Hotspot/utils";
import { WidgetWrapper } from "@learningblocks/interactives/interactive-infographic-components/lib/WidgetWrapper";
import { IntroModal } from "@learningblocks/interactives/interactive-infographic-components/lib/IntroModal";
import { RotateOverlay } from "@learningblocks/interactives/interactive-infographic-components/lib/RotateOverlay";
import { AnimationLegend } from "@learningblocks/interactives/interactive-infographic-components/lib/AnimationLegend";
import { DevMenu } from "@learningblocks/interactives/interactive-infographic-components/lib/DevMenu";
import {
  generateAnimationStateColors,
  getActiveAnimations,
} from "@learningblocks/interactives/interactive-infographic-components/lib/utils/animationColorMapping";
import { withDerivedQuestionStates } from "@learningblocks/interactives/interactive-infographic-components/lib/utils/questionState";

import className from "../style.module.scss";
import { LxButton } from "@labxchange/ui-components/lib/LxButton";

// Blanking the live region and re-filling it on a later tick forces a DOM
// change, so an announcement still registers when it repeats text the region
// held before. Same technique (and delay) as the shared ActionAnnouncer. This
// is a change-detection nudge, NOT a reading budget — assistive tech captures
// the text when the region mutates, so nothing depends on this duration.
const AUTO_TRIGGER_ANNOUNCEMENT_NUDGE_MS = 60;

// How long the announcement stays in the DOM before the region is emptied.
// Purely housekeeping: a `role="status"` region left populated gets read again
// when the learner browses over it. Clearing does not truncate speech already
// queued, so this is deliberately generous.
const AUTO_TRIGGER_ANNOUNCEMENT_CLEAR_MS = 5000;

export function ImageDiscoveryCanvas(props) {
  const intl = useIntl();
  const dispatch = props.requiresRedux ? useDispatch() : null;

  const baseUrl =
    window.CONTENT_BASE_URL ||
    window.location.origin + window.location.pathname;

  const playedStates = useRef(new Set(props.initialPlayedStates || [])); // Persist animation play states across renders

  // Ordering for a section-completing action that also reveals an image (e.g.
  // taking the choice-chamber snapshot): the hotspot triggers immediately (the
  // reveal animation needs that), but its progress report to the Notebook is
  // HELD so "Section N completed" is not announced before the reveal. The held
  // report is flushed the moment the reveal is announced (see the image
  // alt-change effect), so the order is action -> "Snapshot taken!" -> section.
  // A watchdog flushes anyway if the reveal never arrives, so progress can never
  // stall. pendingRevealTriggerRef holds the trigger whose reveal releases it.
  const pendingRevealTriggerRef = useRef(null);
  const heldProgressRef = useRef(null);
  const revealWatchdogRef = useRef(null);

  const handleStateMarkedPlayed = (state) => {
    playedStates.current.add(state);
    if (props.onPlayedStatesUpdate) {
      props.onPlayedStatesUpdate([...playedStates.current]);
    }
  };

  // Animation preload (opt-in): when props.animationCache is supplied we gate
  // the `loaded` flag on every <AnimationEntity> emitting its lottie 'load'.
  const totalAnimations = React.useMemo(
    () => props.sceneData.entities.filter((e) => e.type === "animation").length,
    [props.sceneData.entities]
  );
  const animationLoadedCount = useRef(0);
  const allAnimationsLoadedFiredRef = useRef(false);
  const [allAnimationsLoaded, setAllAnimationsLoaded] = useState(
    () =>
      props.sceneData.entities.filter((e) => e.type === "animation").length ===
      0
  );

  const handleAnimationReady = React.useCallback(() => {
    animationLoadedCount.current += 1;
    if (animationLoadedCount.current >= totalAnimations) {
      setAllAnimationsLoaded(true);
    }
  }, [totalAnimations]);

  const canvasContainerRef = useRef(null);
  const containerRef = useRef(null);
  const [cellSize, setCellSize] = useState(0);
  const [gridSize, setGridSize] = useState({ x: 0, y: 0 });
  const [maxViewX, setMaxViewX] = useState(0);
  const [maxViewY, setMaxViewY] = useState(0);
  const [currentView, setCurrentView] = useState(
    props.sceneData.currentView ?? props.sceneData.mainView
  );
  const [grid, setGrid] = useState([]);
  const [showGrid, setShowGrid] = useState(false);
  const [devView, setDevView] = useState(false);
  const [showAnimationHighlight, setShowAnimationHighlight] = useState(false);
  const [hiddenAnimations, setHiddenAnimations] = useState(new Set());
  const [hiddenAnimationContent, setHiddenAnimationContent] = useState(
    new Set()
  );
  const [showHotspots] = useState(true);
  const [device, setDevice] = useState("mob"); // "mob", "tab", "dsk"
  let timer;
  const [tick, setTick] = useState(0);
  const [closeWidget, setCloseWidget] = useState(false);
  const [introModal, setIntroModal] = useState(props.sceneData.intro !== "");

  const [widgetData, setWidgetData] = useState({
    type: "none",
    active: false,
    hotspotID: 0,
  });
  const [views, setViews] = useState({ ...props.sceneData.views });
  const [hotspots, setHotspots] = useState(() =>
    withDerivedQuestionStates({ ...props.sceneData.hotspots })
  );
  const entities = props.sceneData.entities;
  const [resources, setResources] = useState({});
  const [triggerLog, setTriggerLog] = useState(
    props.sceneData.triggerLog || []
  );
  const [latestHotspotRef, setLatestHotspotRefState] = useState(0);

  const setLatestHotspotRef = (ref) => {
    setLatestHotspotRefState(ref);
  };

  const [lastTriggeredHotspot, setLastTriggeredHotspot] = useState(null);

  // Single consolidated announcement for all screen reader notifications
  // Handles: view navigation, text-body instructions, image alt text changes
  const [screenReaderAnnouncement, setScreenReaderAnnouncement] = useState("");
  // Separate live region for a secondary, non-content announcement (the control
  // an animation reveals on completion) so it can never overwrite a text-body
  // instruction in the shared region.
  const [secondaryAnnouncement, setSecondaryAnnouncement] = useState("");
  // Trigger fired by an animation on completion, consumed on the next effect run
  // to announce the control it reveals. Set only in fireTrigger (animations only,
  // never hotspot clicks) and consumed immediately, so it can never surface on an
  // ordinary interaction.
  const pendingAnimationTriggerRef = useRef(null);
  // Held so the pending announcement survives effect re-runs (a bare setTimeout
  // with an effect-cleanup would be cancelled by any dep change in the delay).
  const animationAnnounceTimerRef = useRef(null);
  const secondaryAnnounceFrameRef = useRef(null);

  // Deliver a message to the secondary region so that repeating the SAME string
  // still announces. React bails out of a state update whose value is
  // Object.is-equal to the current one, so the text node never mutates and
  // assistive technology sees no change — which would make this region a
  // one-shot channel per string (a re-run timer reporting the same completion,
  // or a control revealed again after an undoTrigger/re-fire cycle, would be
  // silent). Unlike the primary region there is no auto-clear effect here, so
  // nothing else would reset it. Blank first, then set on the next frame: two
  // commits, so a repeat is a real DOM mutation.
  const announceSecondary = React.useCallback((message) => {
    if (!message) return;
    cancelAnimationFrame(secondaryAnnounceFrameRef.current);
    setSecondaryAnnouncement("");
    secondaryAnnounceFrameRef.current = requestAnimationFrame(() => {
      setSecondaryAnnouncement(message);
    });
  }, []);

  // --- Auto-trigger state (see the auto-trigger effect below) ---------------
  // An automatic action announces in exactly ONE place. Which place is forced by
  // assistive tech, not by preference: a widget opens as `aria-modal` and screen
  // readers then confine themselves to the dialog subtree, ignoring live regions
  // outside it (confirmed with VoiceOver — the text reached this region on time
  // and was simply never spoken). So an action that opened a widget carries its
  // announcement INSIDE that dialog, as its description; anything else uses this
  // region. Never both.
  //
  // This region is its own rather than sharing the consolidated one above, which
  // has three other producers (view change, text-body reveal at +2s, image-alt
  // change at +2s) that would overwrite an auto-trigger message.
  const [autoTriggerAnnouncement, setAutoTriggerAnnouncement] = useState("");
  // Incremented per announcement so the region below is remounted as a new node
  // each time — see announceAutoTrigger for why that is load-bearing.
  const [autoTriggerAnnouncementKey, setAutoTriggerAnnouncementKey] =
    useState(0);
  // Announcement for an auto-trigger that opened a widget, rendered inside that
  // widget's dialog as its own live region.
  const [autoOpenedWidgetAnnouncement, setAutoOpenedWidgetAnnouncement] =
    useState("");
  // Hotspot ids whose auto-trigger dependencies are satisfied AND have already
  // fired for this edge. Cleared when the dependencies stop being satisfied,
  // which is what re-arms the hotspot.
  const autoTriggerLatchRef = useRef(new Set());
  // Hotspot ids auto-triggered during the current unbroken cascade — see the
  // loop guard in isAutoTriggerEligible. Emptied as soon as a pass resolves
  // nothing, which is what ends a cascade.
  const autoTriggerBurstRef = useRef(new Set());
  // Authoring problems already reported, so a hotspot that keeps re-arming does
  // not repeat the same warning on every edge.
  const autoTriggerWarnedRef = useRef(new Set());
  const autoTriggerAnnouncementTimersRef = useRef([]);
  // Announcement text scheduled but not yet written to the region, so a
  // following announcement merges with it rather than cancelling it unread.
  const autoTriggerPendingTextRef = useRef("");
  // Hotspot whose widget the engine has just asked to open, before that widget
  // is on screen.
  const autoOpenedHotspotIdRef = useRef(null);
  // The same id once its widget is observed open — the focus target to restore
  // when it closes. See the two-phase handoff in the focus effect below.
  const autoOpenFocusTargetRef = useRef(null);
  // Previous value of widgetData.active, so that effect can act on the closing
  // edge rather than on the flag's current value.
  const widgetWasActiveRef = useRef(false);

  // If an image changes in the same view, its alt text should be re-announced.
  // To detect this, we need to track the current state of each image entity and compare on each update.
  const previousEntityStatesRef = useRef(new Map());
  // View at the last alt-announcer run: tells an in-view image reveal (e.g. a
  // snapshot — announce) from a background re-activating on view change (stay silent).
  const previousAnnouncerViewRef = useRef(currentView);

  // Views whose description has been announced: read a view's description only on
  // first entry, not on return. The starting view counts as an entry too — it is
  // added by announceStartingView below, so it is described once and never again.
  const announcedViewDescriptionsRef = useRef(new Set());
  const startingViewAnnouncedRef = useRef(false);
  // Accessible name of the canvas region for the current view. Set to the view's
  // description on first entry, when the focus effect lands there so the
  // description is spoken as the focus announcement.
  const [viewRegionLabel, setViewRegionLabel] = useState("");
  const pendingRegionFocusRef = useRef(false);

  const [dev] = useState(props.sceneData.debug);
  const [loaded, setLoaded] = useState(false);
  // When `animationCache` is supplied, the loaded gate splits in two: images
  // settle (this flag) AND every AnimationEntity emits 'load' (allAnimationsLoaded).
  const [imagesSettled, setImagesSettled] = useState(false);
  const preview = props.preview;

  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => window.innerWidth <= 500
  );

  const [showOrientationPrompt, setShowOrientationPrompt] = useState(false);
  const [orientationPromptDismissed, setOrientationPromptDismissed] = useState(
    () => sessionStorage.getItem("orientationPromptDismissed") === "true"
  );

  // Pause control for looping animations (for accessibility)
  const [pauseLoopingAnimations, setPauseLoopingAnimations] = useState(false);

  // Check if there are any looping animations in the scene
  const hasLoopingAnimations = entities.some(
    (entity) =>
      entity.type === "animation" &&
      entity.states?.some((state) => state.loop === true)
  );

  // Maintain hotspot order if provided (to assist with navigation order)
  const hotspotOrder =
    props.sceneData.hotspotOrder || Object.keys(props.sceneData.hotspots);

  // No-op function for props that don't require action in certain contexts
  const noop = () => undefined;

  useEffect(() => {
    setGridLimits();
    checkDevice();
    if (props.standalone) {
      document.title = `LabXchange | ${intl.formatMessage({
        id: props.sceneData.title,
        defaultMessage: props.sceneData.title,
        description: "",
      })}`;
    }
    if (props.forceStateChangers) {
      forceStateChangers(props.forceStateChangers);
    }
  }, []);

  useEffect(() => {
    const updateCellSize = () => {
      if (containerRef.current && gridSize.x > 0 && maxViewY > 0) {
        const containerHeight = containerRef.current.offsetHeight;
        let newCellHeight;
        if (devView) {
          newCellHeight = containerHeight / maxViewY / 2;
        } else {
          newCellHeight = containerHeight / views[currentView].size.height;
        }
        setCellSize(newCellHeight);
      }
    };

    updateCellSize();

    window.addEventListener("resize", updateCellSize);
    return () => window.removeEventListener("resize", updateCellSize);
  }, [gridSize, maxViewY, currentView, devView]);

  useEffect(() => {
    preloadResources(intl.locale);
  }, [intl.locale]);

  // Single-stage `loaded` gate (matches pre-change behaviour): flip `loaded`
  // as soon as the image preload settles. Animation readiness is tracked
  // separately via the optional `onAllAnimationsLoaded` callback so the
  // ecology-3d preload Provider can drive its own loader removal — but it
  // does NOT gate this engine flag, otherwise a single missed lottie 'load'
  // event would leave the canvas invisibly mounted forever.
  useEffect(() => {
    if (loaded) return;
    if (imagesSettled) {
      setLoaded(true);
    }
  }, [imagesSettled, loaded]);

  // Independently fire `onAllAnimationsLoaded` when every AnimationEntity has
  // emitted lottie 'load' AND images have settled. Best-effort signal kept
  // for backwards compat with the spec; no other code relies on it firing.
  useEffect(() => {
    if (!props.animationCache) return;
    if (allAnimationsLoadedFiredRef.current) return;
    if (imagesSettled && allAnimationsLoaded) {
      allAnimationsLoadedFiredRef.current = true;
      props.onAllAnimationsLoaded?.();
    }
  }, [
    imagesSettled,
    allAnimationsLoaded,
    props.animationCache,
    props.onAllAnimationsLoaded,
  ]);

  useEffect(() => {
    updateTick();
    return () => clearInterval(timer);
  }, [tick]);

  useEffect(() => {
    setTick(0);
    trackProgress();
  }, [currentView, hotspots, triggerLog]);

  // Sync hotspot images from props to local state whenever props.sceneData.hotspots changes
  useEffect(() => {
    const updatedHotspots = { ...hotspots };
    let hasChanges = false;

    Object.keys(props.sceneData.hotspots).forEach((hotspotID) => {
      // Check if image in props exists or not
      const propsHasImage = props.sceneData.hotspots[hotspotID]?.props?.image;
      const stateHasImage = updatedHotspots[hotspotID]?.props?.image;
      if (!propsHasImage && stateHasImage) {
        if (updatedHotspots[hotspotID]?.props) {
          // Remove the image property from props
          const { image, ...restProps } = updatedHotspots[hotspotID].props;
          void image;
          updatedHotspots[hotspotID] = {
            ...updatedHotspots[hotspotID],
            props: { ...restProps },
          };
          hasChanges = true;
        }
      }
    });
    if (hasChanges) {
      setHotspots(updatedHotspots);
    }
  }, [props.sceneData.hotspots]);

  useEffect(() => {
    setTick(0);
    if (showGrid) {
      doTheGriddy();
    }
  }, [currentView, gridSize, showGrid]);

  useEffect(() => {
    const handleNarrowResize = () =>
      setIsNarrowViewport(window.innerWidth <= 500);
    window.addEventListener("resize", handleNarrowResize);
    return () => window.removeEventListener("resize", handleNarrowResize);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const isPortraitOrTooSmall =
        window.innerWidth < 600 && window.innerHeight > window.innerWidth;

      if (isPortraitOrTooSmall && !orientationPromptDismissed) {
        setShowOrientationPrompt(true);
      } else {
        setShowOrientationPrompt(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [orientationPromptDismissed]);

  useEffect(() => {
    if (device === "mob") {
      // let elem = document.getElementById(`canvas_${props.sceneData.id}`);
      // elem.requestFullscreen()
      //   .then(()=>{
      //     screen.orientation.lock("landscape-primary")
      //       .then(() => {
      //         console.log("Orientation locked to horizontal.")
      //       })
      //       .catch((error) => {
      //         console.log(error)
      //       });
      //   })
      //   .catch((err) => {
      //     console.log(
      //       err
      //     );
      //   }
      // );
    }
  }, [device]);

  useEffect(() => {
    if (props.handleSceneUpdate) {
      props.handleSceneUpdate(hotspots, triggerLog, currentView);
    }
  }, [hotspots, triggerLog, currentView]);

  // Clear only view navigation announcements after they've been read
  // Text-body and image alt announcements persist until replaced by new content
  // This ensures screen readers have time to announce longer text and slower speech rates
  useEffect(() => {
    if (!screenReaderAnnouncement) return;

    // Only clear "View changed" announcements - they're short and should be cleared quickly
    if (screenReaderAnnouncement.startsWith("View changed")) {
      const timer = setTimeout(() => {
        setScreenReaderAnnouncement("");
      }, 1000);
      return () => clearTimeout(timer);
    }

    // All other announcements (text-body, image alt) persist until replaced
    // They will naturally be replaced when new announcements come in
  }, [screenReaderAnnouncement]);

  // Move focus after a view transition (accessibility / WCAG 2.4.3 Focus Order).
  //
  // Hotspots outside the current view are unmounted, so a view-changer hotspot
  // disappears on click and browser focus would otherwise fall back to
  // document.body. Inside the simulation iframe a screen reader announces that
  // as the surrounding "main frame", then Tab jumps to the first hotspot — the
  // reported broken focus order. On a real view change we move focus to the
  // first unvisited, interactive hotspot in the destination view (skipping
  // text-body/info and Back/Next/Up/Down controls), falling back to the first
  // focusable hotspot so focus never rests on the frame.
  //
  // A destination hotspot is rendered iff it is visible, and visibility is a
  // pure function of (currentView, hotspots, triggerLog). Keying this effect on
  // those inputs makes it re-run exactly when the target first mounts, so a
  // pending focus request resolves the moment its element appears — no DOM
  // polling or timers. previousViewRef ensures we never grab focus on initial
  // load (or a StrictMode double-mount); the request is cleared once focus moves.
  //
  // Trade-off vs the old capped timer: the retry is unbounded in time. The
  // fallback below (first focusable hotspot) clears the request in any view with
  // a focusable hotspot, so it only lingers on a view with NONE — where a
  // hotspot appearing later (e.g. a customTrigger/MSCQ-complete unlock) will
  // then receive focus on the next hotspots/triggerLog change. That deferred
  // focus is intentional and desirable (move to the newly-available control) and
  // always targets the current view, so it never focus-jumps across views.
  const previousViewRef = useRef(currentView);
  const pendingViewFocusRef = useRef(false);
  // view id -> id of the content view-changer hotspot last used to LEAVE that
  // view. Written by changeView, consumed (and cleared) below when the user
  // navigates back, so focus returns to the hotspot that took them away
  // instead of jumping to an unrelated unvisited hotspot.
  const returnFocusHotspotByViewRef = useRef({});

  const focusableHotspot = (id) => {
    const element = document.getElementById(`hotspotBtn_${id}`);
    if (!element || element.disabled || element.tabIndex < 0) {
      return null;
    }
    return element;
  };
  const firstFocusable = (ids) => {
    for (const id of ids) {
      const element = focusableHotspot(id);
      if (element) return element;
    }
    return null;
  };

  // The hotspot focus should land on for the current view: the first unvisited
  // interactive one, else any focusable one. Shared with the auto-open focus
  // effect, which needs to resolve a target immediately rather than by queuing a
  // request for the view-transition effect below — that effect only re-runs on a
  // view/hotspot/trigger change, and a bare widget close is none of those.
  const findViewFocusTarget = () => {
    const orderedIds = hotspotOrder?.length
      ? hotspotOrder
      : Object.keys(hotspots);
    const unvisitedInteractiveIds = orderedIds.filter(
      (id) =>
        hotspots[id] &&
        !hotspots[id].triggered &&
        isNavigableContentHotspot(hotspots[id])
    );
    return (
      firstFocusable(unvisitedInteractiveIds) || firstFocusable(orderedIds)
    );
  };

  // Which hotspot a view transition should put focus on. Shared by the effect
  // below and the canvas region's Tab handler, so both honour the same policy:
  // return-to-trigger, else first unvisited interactive hotspot, else first
  // focusable one. Consumes the return-to-trigger record, so a stale entry can
  // never redirect a later visit.
  const resolveViewFocusTarget = () => {
    const returnHotspotId = returnFocusHotspotByViewRef.current[currentView];
    if (returnHotspotId !== undefined) {
      delete returnFocusHotspotByViewRef.current[currentView];
      const returnTarget = focusableHotspot(returnHotspotId);
      if (returnTarget) {
        return returnTarget;
      }
    }

    return findViewFocusTarget();
  };

  useEffect(() => {
    if (previousViewRef.current !== currentView) {
      previousViewRef.current = currentView;
      pendingViewFocusRef.current = true;
    }
    if (!pendingViewFocusRef.current) {
      return;
    }

    // First entry to a view that has a description: land on the canvas region,
    // whose accessible name IS that description, so it is spoken as the focus
    // announcement — ahead of any hotspot. Putting it in the polite live region
    // instead cannot achieve that ordering: "polite" is defined as "wait until
    // the user is idle", so it is always spoken AFTER the announcement the
    // focus move produces, however early the text is written to the DOM. Tab
    // from the region is then native: it reaches the pause/play control, or the
    // first hotspot in DOM order when the scene has no looping animations.
    if (pendingRegionFocusRef.current) {
      // While the scene is still loading the region is named "Loading", so
      // focusing it now would announce that instead of the description. The
      // request survives in the refs and this effect re-runs when `loaded` flips.
      if (!containerRef.current || !loaded) {
        return;
      }
      pendingRegionFocusRef.current = false;
      pendingViewFocusRef.current = false;
      containerRef.current.focus();
      return;
    }

    const target = resolveViewFocusTarget();
    if (target) {
      pendingViewFocusRef.current = false;
      target.focus();
    }
    // Deps MUST stay in sync with every input to hotspot visibility (see
    // checkHotspotVisibilityDependencies / checkHotspotHiddenDependencies): the
    // pending request only resolves on a re-run, so a new visibility input not
    // listed here would let focus silently fall back to the iframe body.
    // introModal is included so the request set by closeIntroModal resolves
    // right after the modal unmounts, and `loaded` so an on-load region focus
    // resolves as soon as the region carries the description rather than
    // "Loading".
  }, [currentView, hotspots, triggerLog, introModal, loaded]);

  // Watch for MSCQ questions becoming complete and trigger hotspots
  useEffect(() => {
    Object.keys(hotspots).forEach((hotspotID) => {
      const hotspot = hotspots[hotspotID];
      // Check if this is an MSCQ that just became complete and hasn't been triggered yet
      if (
        hotspot.props?.type === "MSCQ" &&
        hotspot.props?.complete === true &&
        !hotspot.triggered &&
        !hotspot.preventSelfCompletion
      ) {
        // Trigger this hotspot
        const newHotspots = { ...hotspots };
        newHotspots[hotspotID] = {
          ...newHotspots[hotspotID],
          triggered: true,
        };
        setHotspots(newHotspots);
      }
    });
  }, [hotspots]);

  // NOTE: a text-body hotspot's instruction is deliberately NOT pushed into the
  // live region. It is a static panel, not an event: it has no trigger moment,
  // and it becomes "newly visible" again every time its view is re-entered, so
  // announcing it spoke the panel's text at whoever happened to hold focus
  // (typically the hotspot the view transition had just focused). The panel is a
  // focusable hotspot whose text is its accessible name (see Hotspot's ariaLabel),
  // so a screen reader reads it when — and only when — the user reaches it.

  // Announce image entity alt text changes to screen readers
  useEffect(() => {
    const previousStates = previousEntityStatesRef.current;
    const currentStates = new Map();

    // Check each entity's current state
    entities.forEach((entity, entityIndex) => {
      // Only process image entities
      if (entity.type !== "image") return;

      const currentStateIndex = checkEntityState(entity);
      currentStates.set(entityIndex, currentStateIndex);

      // Skip if no active state
      if (currentStateIndex === null) return;

      const previousStateIndex = previousStates.get(entityIndex);

      // Announce an image state change only when it happens within the current
      // view. Any change that coincides with a view transition is suppressed:
      // navigation is already covered by the on-entry view announcement
      // (announceViewEntry) and the focus move, and a background that persists
      // across views (e.g. the home view's) would otherwise re-announce its alt
      // every time the user returns from a hotspot.
      const viewChanged = previousAnnouncerViewRef.current !== currentView;
      if (
        previousStateIndex !== undefined &&
        previousStateIndex !== currentStateIndex &&
        !viewChanged
      ) {
        const currentState = entity.states[currentStateIndex];
        const altText = currentState.props.altText;

        // Announce if alt text exists
        if (altText) {
          // Format alt text for i18n
          const announcement = intl.formatMessage({
            id: altText,
            defaultMessage: altText,
            description: "Image alt text",
          });

          // Announce synchronously as soon as the image reveal is detected — e.g.
          // the choice-chamber snapshot's "Snapshot taken!" the moment ANIM_SNAPSHOT
          // fires. A polite live region queues behind (never clips) any concurrent
          // focus announcement, so no delay is needed to protect focus; the old 2s
          // delay only made the reveal trail a same-tick section-complete/notebook
          // announcement by a long, confusing pause.
          setScreenReaderAnnouncement(announcement);

          // If a section-completing action was waiting on this reveal, release
          // the held "Section N completed" report NOW — in a later tick — so it
          // announces in a commit AFTER this reveal announcement, and the user
          // hears "Snapshot taken!" first, then "Section N completed".
          if (pendingRevealTriggerRef.current) {
            setTimeout(flushHeldProgress, 0);
          }

          // Only announce first change if multiple images change simultaneously
          return;
        }
      }
    });

    // Update tracked states for next comparison
    previousEntityStatesRef.current = currentStates;
    previousAnnouncerViewRef.current = currentView;
  }, [entities, hotspots, currentView, triggerLog, intl]);

  // When an animation completes and fires a trigger (e.g. the choice-chamber
  // timer firing ANIM_CLOCK), announce the label of the control that trigger
  // reveals in the current view (e.g. "Take photo") so screen-reader users know
  // a new action is available. Scoped to animation-fired triggers (fireTrigger),
  // and matched to hotspots whose visibility depends on that trigger, so it never
  // fires on ordinary hotspot interactions. Uses the secondary live region.
  useEffect(() => {
    const trigger = pendingAnimationTriggerRef.current;
    if (!trigger) return;
    // Consume the trigger on this run. A hotspot's visibility is a pure function
    // of triggerLog, which already contains the fired trigger, so the control it
    // reveals is resolvable right now. We deliberately do NOT keep the trigger
    // latched across renders: a persisted latch could otherwise surface ~2s after
    // a later, unrelated interaction that happens to satisfy the reveal.
    pendingAnimationTriggerRef.current = null;

    const orderedIds = hotspotOrder?.length
      ? hotspotOrder
      : Object.keys(hotspots);
    for (const id of orderedIds) {
      const hotspot = hotspots[id];
      if (!hotspot?.tooltipText) continue;
      const revealedByTrigger =
        hotspot.visibility_dependencies?.customTriggers?.includes(trigger);
      if (!revealedByTrigger) continue;
      const visible =
        checkHotspotVisibilityDependencies(hotspot) &&
        checkHotspotHiddenDependencies(hotspot);
      if (!visible) continue;

      // Schedule the announcement via a ref-held timer (NOT an effect-cleanup
      // timer) so a re-render inside the 2s delay window can't cancel it. Later
      // re-runs early-return (trigger is null) and leave the timer alone.
      const announcement = intl.formatMessage({
        id: hotspot.tooltipText,
        defaultMessage: hotspot.tooltipText,
        description: "Control revealed when an animation completes",
      });
      // Last-wins is deliberate: if a second animation completes inside the 2s
      // window, the newer control is the one the user can act on, so the older
      // pending label is dropped rather than queued.
      clearTimeout(animationAnnounceTimerRef.current);
      animationAnnounceTimerRef.current = setTimeout(() => {
        announceSecondary(announcement);
      }, 2000);
      return;
    }
  }, [triggerLog, hotspots, currentView, intl, hotspotOrder]);

  // Clear any pending timers on unmount. The reveal watchdog matters as much as
  // the announcement timer: it is armed for 6s and calls flushHeldProgress,
  // which reports progress — unmounting inside that window (routine on a
  // notebook section change) would fire it against a dead component.
  useEffect(
    () => () => {
      clearTimeout(animationAnnounceTimerRef.current);
      clearTimeout(revealWatchdogRef.current);
      cancelAnimationFrame(secondaryAnnounceFrameRef.current);
    },
    []
  );

  // Data-driven auto-trigger: perform a hotspot's own action FOR the user when
  // its `auto_trigger_dependencies` are satisfied, paired with a screen-reader
  // announcement (`autoTriggerAnnouncement`).
  //
  // Re-arm is EDGE-TRIGGERED. A hotspot fires when its dependency evaluation
  // transitions unsatisfied -> satisfied and is then latched until the
  // evaluation goes unsatisfied again. This is what stops a closed widget from
  // instantly re-opening: closing it does not change any state the dependencies
  // read, so the latch still holds. Leaving the view does clear it, so a
  // view-scoped dependency fires again on a later visit — while a dependency on
  // another hotspot being `triggered` latches permanently (that flag is
  // monotonic) and therefore fires exactly once. Re-arm behaviour is thus a
  // consequence of the shape of the authored dependency, not a separate switch.
  //
  // Candidates are walked in `hotspotOrder`, the authoritative order in this
  // engine. Every eligible one resolves, stopping at the first that opens a
  // widget because the engine has a single `widgetData` slot; the rest keep
  // their place for a later pass.
  //
  // Announcing is uniform: every hotspot that fires contributes its
  // announcement to the one live region, regardless of what its action did, and
  // nothing else announces on its behalf. Announcements resolving in the same
  // pass are COALESCED into a single message — successive writes to one atomic
  // region replace each other, so joining them is what makes each audible,
  // rather than spacing them out on a timer.
  //
  // Declared AFTER the view-transition focus effect on purpose: effects run in
  // declaration order, so focus has already settled in the destination view by
  // the time an auto-opened dialog captures the element to restore focus to.
  useEffect(() => {
    const latched = autoTriggerLatchRef.current;
    const armed = [];

    // Bookkeeping pass — runs even when nothing can fire, so a dependency that
    // goes false while a widget is open re-arms without waiting for a later
    // state change.
    hotspotOrder.forEach((hotspotID) => {
      const hotspot = hotspots[hotspotID];
      if (!hotspot) return;

      if (!checkAutoTriggerDependencies(hotspot)) {
        latched.delete(hotspotID);
        return;
      }
      if (!latched.has(hotspotID)) armed.push(hotspotID);
    });

    // Nothing may auto-trigger while the scene is still loading, while the intro
    // modal is up, or while any widget is open or closing.
    if (!loaded || introModal || widgetData.active || closeWidget) {
      return;
    }

    // EXACTLY ONE auto-trigger resolves per pass, first in `hotspotOrder`. Any
    // other armed hotspot stays unlatched and fires on a later pass, with its own
    // announcement in whichever channel is audible by then.
    //
    // This is a rule, not an optimisation. Resolving two in one pass meant a
    // non-widget announcement could be written to the canvas region by the same
    // pass that opened a widget — and `aria-modal` makes screen readers ignore
    // that region, so the announcement was silently never spoken (WCAG 4.1.3).
    // Stopping at the first fire removes that combination structurally rather
    // than routing around it.
    let firedAny = false;
    for (const hotspotID of armed) {
      const hotspot = hotspots[hotspotID];
      if (!isAutoTriggerEligible(hotspotID, hotspot)) continue;

      if (firedAny) {
        // Never silently: two hotspots armed at once is authoring this engine
        // does not support, and the learner would get two announcements in quick
        // succession where the second can clip the first.
        warnOnceForAutoTrigger(
          `simultaneous:${hotspotID}`,
          `Hotspot "${hotspotID}" was ready to auto-trigger in the same pass as another one. Only one resolves at a time, in hotspot order, so this one follows on a later pass. Authoring two simultaneous auto-triggers is not supported.`
        );
        break;
      }

      latched.add(hotspotID);
      autoTriggerBurstRef.current.add(hotspotID);
      firedAny = true;

      const announcement = getAutoTriggerAnnouncement(hotspotID, hotspot);

      if (dispatchAutoTrigger(hotspotID, hotspot)) {
        // Remember which hotspot to hand focus back to when this widget closes.
        autoOpenedHotspotIdRef.current = hotspotID;

        // Fold in an announcement that is scheduled but unread: this widget's
        // dialog is `aria-modal`, so it is about to hide the canvas region from
        // assistive tech and text left there would never be spoken.
        //
        // The one-per-pass rule cannot catch this, because the two are not in
        // one pass — an announce-only hotspot whose completion is what ARMED
        // this one resolves a single commit earlier, well inside the
        // announcement window. Verified: without this, the canvas region held
        // the first announcement while the dialog was already open.
        const pending = takePendingAutoTriggerAnnouncement();

        // Set AFTER dispatch, because `updateWidgetData` clears it — that is
        // what stops a manually opened widget inheriting a stale automatic
        // announcement.
        setAutoOpenedWidgetAnnouncement(
          pending ? `${pending} ${announcement}` : announcement
        );
      } else {
        announceAutoTrigger(announcement);
      }
    }

    if (!firedAny) {
      // A pass that resolved nothing means the cascade has come to rest, so the
      // loop guard forgets it. This only ever makes the guard more permissive: a
      // true runaway re-renders straight into its next hop and never reaches a
      // resting pass.
      autoTriggerBurstRef.current.clear();
    }
  }, [
    hotspots,
    currentView,
    triggerLog,
    hotspotOrder,
    loaded,
    introModal,
    widgetData.active,
    closeWidget,
    intl,
  ]);

  // Place focus after an auto-opened widget closes (WCAG 2.4.3 Focus Order).
  // The user never clicked anything, so `latestHotspotRef` still points at
  // whatever they last activated — possibly in a previous view and unmounted,
  // which drops focus to the iframe body. Put focus on the hotspot that was
  // auto-triggered, or defer to the view-transition restoration if it has since
  // unmounted (e.g. postTrigger "hidden").
  //
  // Keyed on the widget unmounting rather than done in `handleCloseWidget`,
  // whose timeout still runs while the dialog is mounted and its focus trap
  // active. The keyboard-only heuristic used for manual closes does not apply
  // here: there was no pointer interaction that could leave a stuck focus ring
  // behind.
  //
  // Two-phase handoff, because "the auto-opened widget closed" cannot be read
  // off a single flag. The auto-trigger effect above runs earlier in the SAME
  // effect pass, and the pass it usually fires on is the one closing the widget
  // the user opened by hand — an `active` true -> false edge with a freshly-set
  // target. Consuming the target there would focus the hotspot while the next
  // dialog's focus trap is active (which the trap immediately undoes) and leave
  // nothing to focus on the real close, dropping focus to the iframe body. So
  // the target is only ADOPTED once its widget is observed open, and only the
  // adopted target is consumed on a closing edge.
  useEffect(() => {
    const wasActive = widgetWasActiveRef.current;
    widgetWasActiveRef.current = widgetData.active;

    if (widgetData.active) {
      if (autoOpenedHotspotIdRef.current !== null) {
        autoOpenFocusTargetRef.current = autoOpenedHotspotIdRef.current;
        autoOpenedHotspotIdRef.current = null;
      }
      return;
    }

    if (!wasActive) return;
    const hotspotID = autoOpenFocusTargetRef.current;
    if (hotspotID === null) return;
    autoOpenFocusTargetRef.current = null;

    // Deferred so it lands after the dialog's own restore-focus-on-close, which
    // targets whatever was focused before it opened — for an auto-opened widget
    // that is often an element that has since unmounted, so letting it win means
    // focus falls to the iframe body.
    const frame = requestAnimationFrame(() => {
      // Resolve a target NOW rather than queuing pendingViewFocusRef for the
      // view-transition effect. That effect only re-runs on a
      // view/hotspot/trigger change, and the change this close produced (the
      // hotspot becoming triggered) already happened one commit earlier — so a
      // request left here is never consumed and focus falls to the iframe body.
      // Reachable whenever the auto-triggered hotspot unmounts on trigger
      // (postTrigger "hidden") or becomes disabled ("inactive").
      const target = focusableHotspot(hotspotID) || findViewFocusTarget();
      if (target) {
        pendingViewFocusRef.current = false;
        target.focus();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [widgetData.active]);

  // Returns true if rect overlaps the current view rectangle (in grid units)
  const rectOverlapsView = (rect, view) => {
    const vx1 = view.x_offset;
    const vy1 = view.y_offset;
    const vx2 = view.x_offset + view.size.width - 1;
    const vy2 = view.y_offset + view.size.height - 1;

    const ex1 = rect.x;
    const ey1 = rect.y;
    const ex2 = rect.x + rect.w - 1;
    const ey2 = rect.y + rect.h - 1;

    const overlapX = ex1 <= vx2 && ex2 >= vx1;
    const overlapY = ey1 <= vy2 && ey2 >= vy1;

    return overlapX && overlapY;
  };

  // Props to apply to a wrapper that should be hidden from assistive tech
  const getA11yHiddenProps = (shouldHide) => {
    if (!shouldHide) return {};

    // `inert` is supported in modern browsers; when present it prevents focus/interaction
    // React will pass unknown boolean attrs weirdly, so set inert as an empty string.
    return {
      "aria-hidden": true,
      inert: "",
    };
  };

  // Check triggerLog with support for negation (! prefix)
  const isTriggerActive = (triggerLog, triggerName) => {
    if (typeof triggerName === "string" && triggerName.startsWith("!")) {
      return !triggerLog.includes(triggerName.slice(1));
    } else {
      return triggerLog.includes(triggerName);
    }
  };

  const loadImage = (src, lng) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const imageUrl = src.includes(window.location.origin)
        ? src
        : `${baseUrl}locales/${lng}/images/${src}`;
      image.src = imageUrl;
      image.onload = () => {
        resolve({ src: src, resources: image, lng: lng });
      };
      image.onerror = () => {
        const fallbackImageUrl = src.includes(window.location.origin)
          ? src
          : `${baseUrl}locales/en/images/${src}`;
        image.src = fallbackImageUrl;
        image.onload = () => {
          resolve({ src: src, resources: image, lng: "en" });
        };
        image.onerror = () => {
          reject(new Error(`Failed to load ${fallbackImageUrl}`));
        };
      };
    });
  };

  const preloadResources = (lng) => {
    const updatedResources = resources;

    const promises_entities = props.sceneData.entities.reduce((acc, entity) => {
      if (entity.type === "image") {
        entity.states.forEach((data) => {
          acc.push(loadImage(data.props.src, lng));
        });
      }
      return acc;
    }, []);

    const promises_widgets = Object.values(props.sceneData.hotspots).reduce(
      (acc, hotspot) => {
        const type = hotspot.props.type;
        const validTypes = [
          "learn-more",
          "CSCQ",
          "MSCQ",
          "TABLE_QUESTION",
          "Slider",
        ];

        if (validTypes.includes(type)) {
          const imgSrc = hotspot?.props?.image?.src || "";
          if (imgSrc !== "") {
            acc.push(loadImage(imgSrc, lng));
          }
        }

        return acc;
      },
      []
    );

    Promise.allSettled([...promises_entities, ...promises_widgets])
      .then((results) => {
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            const { src, resources } = result.value;
            updatedResources[`${src}_${lng}`] = resources;
          }
        });
        setResources(updatedResources);
        setImagesSettled(true);
      })
      .catch((error) => {
        console.error("Error preloading resource:", error);
      });
  };

  const updateTick = () => {
    timer =
      !timer &&
      setInterval(() => {
        setTick((prevTick) => prevTick + 1);
      }, 10000);
  };

  const checkDevice = () => {
    const viewWidth = screen.width;
    if (viewWidth > 1024) {
      setDevice("dsk");
    } else if (viewWidth <= 1024 && viewWidth > 720) {
      setDevice("tab");
    } else {
      setDevice("mob");
    }
  };

  const setGridLimits = () => {
    let x_max = 0;
    let y_max = 0;
    Object.keys(views).map((viewID) => {
      const view = views[viewID];
      if (view.size.height > y_max) {
        y_max = view.size.height;
      }
      if (view.size.width > x_max) {
        x_max = view.size.width;
      }
    });
    setMaxViewX(x_max);
    setMaxViewY(y_max);
    setGridSize({ x: x_max * Object.keys(views).length, y: y_max });
  };

  // Report a held section-completing progress update to the Notebook. Called
  // once the reveal has been announced (or by the watchdog), so the resulting
  // "Section N completed" announcement lands AFTER "Snapshot taken!".
  const flushHeldProgress = () => {
    if (revealWatchdogRef.current) {
      clearTimeout(revealWatchdogRef.current);
      revealWatchdogRef.current = null;
    }
    pendingRevealTriggerRef.current = null;
    const held = heldProgressRef.current;
    heldProgressRef.current = null;
    if (held && props.trackProgress) {
      props.updateProgress(held.count, held.hotspot);
    }
  };

  const trackProgress = () => {
    let maxActions = 0;
    let triggeredActions = 0;
    let triggeredActionsOverall = 0;
    Object.keys(hotspots).map((hotspotID) => {
      const hotspot = hotspots[hotspotID];
      if (
        (props.trackProgress &&
          hotspot.triggered &&
          hotspot.postTrigger !== "no-change") ||
        (hotspot.props.complete && hotspot.postTrigger !== "no-change")
      ) {
        triggeredActionsOverall++;
      }
      if (
        hotspot.visibility_dependencies.views.includes(currentView) ||
        hotspot.visibility_dependencies.views.length === 0
      ) {
        if (hotspot.postTrigger !== "no-change") {
          maxActions++;
          if (hotspot.triggered) {
            triggeredActions++;
          }
        }
      }
    });
    if (props.trackProgress) {
      if (pendingRevealTriggerRef.current) {
        // Hold this report: a section-completing action with a pending image
        // reveal is in flight. flushHeldProgress() releases it once the reveal
        // is announced, so "Section N completed" follows "Snapshot taken!".
        heldProgressRef.current = {
          count: triggeredActionsOverall,
          hotspot: lastTriggeredHotspot,
        };
      } else {
        props.updateProgress(triggeredActionsOverall, lastTriggeredHotspot);
      }
    }
    if (triggeredActions === maxActions) {
      const newViews = { ...views };

      if (currentView === props.sceneData.mainView) {
        let complete = true;
        Object.keys(views).map((viewID, key) => {
          const view = views[viewID];
          if (key !== 0 && !view.complete) {
            complete = false;
          }
        });
        if (complete) {
          newViews[currentView].complete = true;
          setViews({ ...newViews });
        }
      } else {
        newViews[currentView].complete = true;
        setViews({ ...newViews });
      }
    }
  };

  const checkEntityState = (entity, viewOverride) => {
    const view = viewOverride ?? currentView;
    let activeState = null;

    // Iterate through each defined state until hase dependencies that are met
    entity.states.forEach((state, key) => {
      if (activeState !== null) return; // previous state conditions met

      const {
        views = [],
        hotspots: hsDeps = [],
        customTriggers = [],
      } = state.state_dependencies;

      // 1) View dependency
      if (views.length > 0 && !views.includes(view)) {
        return;
      }

      // 2) Hotspot-based dependencies
      if (hsDeps.length > 0) {
        const allHotspotsMet = hsDeps.every((dep) => {
          const target = hotspots[dep.id];
          if (!target) return false;

          return Object.entries(dep.requirement).every(
            ([prop, expectedVal]) => {
              const actual =
                prop === "props" ? target.props.currentState : target[prop];
              const expected =
                prop === "props" ? expectedVal.currentState : expectedVal;
              return actual === expected;
            }
          );
        });

        if (!allHotspotsMet) {
          return;
        }
      }

      // 3) Custom-trigger dependencies
      if (customTriggers.length > 0) {
        const allFired = customTriggers.every((name) =>
          isTriggerActive(triggerLog, name)
        );
        if (!allFired) {
          return;
        }
      }

      // All checks passed - active state
      activeState = key;
    });

    return activeState;
  };

  const animationColorMap = React.useMemo(() => {
    return generateAnimationStateColors(entities);
  }, [entities]);

  const activeAnimations = React.useMemo(() => {
    return getActiveAnimations(entities, checkEntityState);
  }, [entities, currentView, hotspots, triggerLog]);

  const toggleAnimationHighlight = (animId, stateIndex) => {
    const colorKey = `${animId}-${stateIndex}`;
    setHiddenAnimations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(colorKey)) {
        newSet.delete(colorKey);
      } else {
        newSet.add(colorKey);
      }
      return newSet;
    });
  };

  const toggleAnimationContent = (animId, stateIndex) => {
    const colorKey = `${animId}-${stateIndex}`;
    setHiddenAnimationContent((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(colorKey)) {
        newSet.delete(colorKey);
      } else {
        newSet.add(colorKey);
      }
      return newSet;
    });
  };

  const doTheGriddy = () => {
    const griddy = [];
    for (let y = 0; y < views[currentView].size.height; y++) {
      for (let x = 0; x < views[currentView].size.width; x++) {
        griddy.push({
          x: x + views[currentView].x_offset,
          y: y + views[currentView].y_offset,
        });
      }
    }
    setGrid(griddy);
  };

  const checkHotspotVisibilityDependencies = (hotspot) => {
    let visible = true;

    if (
      !hotspot.visibility_dependencies.views.includes(currentView) &&
      hotspot.visibility_dependencies.views.length !== 0
    ) {
      visible = false;
    }

    if (hotspot.triggered && hotspot.postTrigger === "hidden") {
      visible = false;
    }

    for (let i = 0; i < hotspot.visibility_dependencies.hotspots.length; i++) {
      for (const property in hotspot.visibility_dependencies.hotspots[i]
        .requirement) {
        if (property !== "props") {
          if (
            hotspots[hotspot.visibility_dependencies.hotspots[i].id][
              property
            ] !==
            hotspot.visibility_dependencies.hotspots[i].requirement[property]
          ) {
            visible = false;
          }
        } else {
          if (
            hotspots[hotspot.visibility_dependencies.hotspots[i].id][property]
              .currentState !==
            hotspot.visibility_dependencies.hotspots[i].requirement[property]
              .currentState
          ) {
            visible = false;
          }
        }
      }
    }

    if (Object.hasOwn(hotspot.visibility_dependencies, "customTriggers")) {
      let customTriggerPass = true;
      hotspot.visibility_dependencies.customTriggers.map((triggerName) => {
        if (!isTriggerActive(triggerLog, triggerName)) {
          customTriggerPass = false;
        }
      });
      if (!customTriggerPass) {
        visible = false;
      }
    }

    return visible;
  };

  const checkHotspotHiddenDependencies = (hotspot) => {
    let visible = true;

    if (
      !hotspot.hidden_dependencies.views.includes(currentView) &&
      hotspot.hidden_dependencies.views.length !== 0
    ) {
      visible = false;
    }

    if (hotspot.triggered && hotspot.postTrigger === "hidden") {
      visible = false;
    }

    const visibilityArr = [];
    for (let i = 0; i < hotspot.hidden_dependencies.hotspots.length; i++) {
      for (const property in hotspot.hidden_dependencies.hotspots[i]
        .requirement) {
        if (property !== "props") {
          if (
            hotspots[hotspot.hidden_dependencies.hotspots[i].id][property] ===
            hotspot.hidden_dependencies.hotspots[i].requirement[property]
          ) {
            visibilityArr.push(true);
          } else {
            visibilityArr.push(false);
          }
        } else {
          if (
            hotspots[hotspot.hidden_dependencies.hotspots[i].id][property]
              .currentState ===
            hotspot.hidden_dependencies.hotspots[i].requirement[property]
              .currentState
          ) {
            visibilityArr.push(true);
          } else {
            visibilityArr.push(false);
          }
        }
      }
    }

    if (visibilityArr.length > 0) {
      visible = visibilityArr.includes(false);
    }

    if (Object.hasOwn(hotspot.hidden_dependencies, "customTriggers")) {
      let customTriggerPass = false;
      hotspot.hidden_dependencies.customTriggers.map((triggerName) => {
        if (isTriggerActive(triggerLog, triggerName)) {
          customTriggerPass = true;
        }
      });
      if (customTriggerPass) {
        visible = false;
      }
    }

    return visible;
  };

  const checkHotspotLockedDependencies = (hotspot) => {
    let locked = false;

    if (hotspot.triggered && hotspot.postTrigger === "hidden") {
      locked = true;
    }

    for (let i = 0; i < hotspot.locked_dependencies.hotspots.length; i++) {
      for (const property in hotspot.locked_dependencies.hotspots[i]
        .requirement) {
        if (property !== "props") {
          if (
            hotspots[hotspot.locked_dependencies.hotspots[i].id][property] !==
            hotspot.locked_dependencies.hotspots[i].requirement[property]
          ) {
            locked = true;
          }
        } else {
          if (
            hotspots[hotspot.locked_dependencies.hotspots[i].id][property]
              .currentState !==
            hotspot.locked_dependencies.hotspots[i].requirement[property]
              .currentState
          ) {
            locked = true;
          }
        }
      }
    }

    if (Object.hasOwn(hotspot.locked_dependencies, "customTriggers")) {
      let customTriggerPass = true;
      hotspot.locked_dependencies.customTriggers.map((triggerName) => {
        if (!isTriggerActive(triggerLog, triggerName)) {
          customTriggerPass = false;
        }
      });
      if (!customTriggerPass) {
        locked = true;
      }
    }

    return locked;
  };

  // Evaluate a hotspot's `auto_trigger_dependencies` — the same
  // {views, hotspots, customTriggers} shape as the visibility/hidden/locked/
  // complete blocks.
  //
  // NOTE the deliberate inversion of this engine's usual convention: an absent
  // or wholly empty block means "never auto-trigger". Every other dependency
  // block treats empty as permissive (empty `views` = visible in every view),
  // so inheriting that convention here would auto-fire every hotspot of every
  // existing scene the moment this shipped. Authoring emits `null` when blank.
  const checkAutoTriggerDependencies = (hotspot) => {
    const deps = hotspot.auto_trigger_dependencies;
    if (!deps) return false;

    const { views = [], hotspots: atHotspots = [], customTriggers = [] } = deps;

    if (
      views.length === 0 &&
      atHotspots.length === 0 &&
      customTriggers.length === 0
    ) {
      return false;
    }

    if (
      views.length > 0 &&
      !views.some((view) => Number(view) === Number(currentView))
    ) {
      return false;
    }

    for (const { id: depID, requirement } of atHotspots) {
      const target = hotspots[depID];
      if (!target) return false;

      for (const property in requirement) {
        const actual =
          property === "props" ? target.props.currentState : target[property];
        const expected =
          property === "props"
            ? requirement.props.currentState
            : requirement[property];
        if (actual !== expected) return false;
      }
    }

    if (
      customTriggers.length > 0 &&
      !customTriggers.every((name) => isTriggerActive(triggerLog, name))
    ) {
      return false;
    }

    return true;
  };

  // Announce a non-widget automatic action. No throttle and no "time to read"
  // budget: assistive tech captures a live region's text the moment it mutates
  // and speaks it in full at whatever rate the learner has set, so the only
  // timing that matters is forcing a DOM change (so repeated text still
  // registers) and eventually emptying the region so it is not re-read during
  // page browsing.
  const announceAutoTrigger = (text) => {
    autoTriggerAnnouncementTimersRef.current.forEach(clearTimeout);
    // Bump the key so the region is a BRAND-NEW node for every announcement,
    // then fill it a beat later. Blanking and refilling one long-lived node is
    // not enough: verified with VoiceOver, a repeat of the same sentence into the
    // same region node is dropped even when assertive, while the announcement
    // rendered inside a widget — whose region is a fresh node each time, because
    // WidgetWrapper unmounts on close — is spoken reliably. Replacing the node
    // reproduces that, so a learner revisiting a view hears it again.
    setAutoTriggerAnnouncementKey((key) => key + 1);
    setAutoTriggerAnnouncement("");

    // Merge with an announcement that has not reached the DOM yet instead of
    // replacing it. Actions resolve one per pass, but consecutive passes can be
    // a single commit apart — well inside the nudge window — so the earlier
    // text would otherwise be cancelled before any screen reader could see it.
    // Once flushed, replacing is correct: it has already been spoken.
    const merged = autoTriggerPendingTextRef.current
      ? `${autoTriggerPendingTextRef.current} ${text}`
      : text;
    autoTriggerPendingTextRef.current = merged;

    const setTimer = setTimeout(() => {
      autoTriggerPendingTextRef.current = "";
      setAutoTriggerAnnouncement(merged);
    }, AUTO_TRIGGER_ANNOUNCEMENT_NUDGE_MS);
    const clearTimer = setTimeout(
      () => setAutoTriggerAnnouncement(""),
      AUTO_TRIGGER_ANNOUNCEMENT_CLEAR_MS
    );
    autoTriggerAnnouncementTimersRef.current = [setTimer, clearTimer];
  };

  useEffect(
    () => () => autoTriggerAnnouncementTimersRef.current.forEach(clearTimeout),
    []
  );

  // Remove and return announcement text that is scheduled but has not reached
  // the region yet, so a caller can redirect it somewhere the learner will
  // actually hear it. Returns "" once it has been written, because by then it
  // has been spoken and moving it would repeat it.
  const takePendingAutoTriggerAnnouncement = () => {
    const pending = autoTriggerPendingTextRef.current;
    if (!pending) return "";
    autoTriggerAnnouncementTimersRef.current.forEach(clearTimeout);
    autoTriggerAnnouncementTimersRef.current = [];
    autoTriggerPendingTextRef.current = "";
    setAutoTriggerAnnouncement("");
    return pending;
  };

  const warnOnceForAutoTrigger = (key, message) => {
    if (autoTriggerWarnedRef.current.has(key)) return;
    autoTriggerWarnedRef.current.add(key);
    console.warn(message);
  };

  // Whether an armed hotspot may actually be activated right now. A hotspot that
  // fails these checks is left UNLATCHED so it still fires once it becomes
  // eligible (e.g. when it unlocks) rather than losing its turn.
  const isAutoTriggerEligible = (hotspotID, hotspot) => {
    // Loop guard. The edge latch stops a hotspot repeating while its
    // dependencies hold, but an auto-triggered action can un-satisfy its own
    // dependency and re-satisfy it via another hotspot — two auto-triggered
    // view-changers pointing into each other's views bounce forever with no
    // learner input, because each hop genuinely re-arms the other. Each hop
    // re-renders immediately, so unguarded this hangs the page.
    //
    // What separates that from legitimate use is that a runaway never pauses: a
    // cascade of auto-triggers reacting to one another fires each hotspot at
    // most once, so a hotspot firing TWICE inside one unbroken cascade is a
    // cycle. The cascade ends the moment a pass resolves nothing (see below),
    // which is why a genuine re-arm on a later visit is unaffected — nothing is
    // capped over a session and no per-hotspot quota exists.
    //
    // Deliberately not keyed on input events: a screen reader activating a
    // control need not produce keydown or pointerdown, and a guard that misread
    // those learners as loops would break the feature for exactly the people it
    // exists for.
    if (autoTriggerBurstRef.current.has(hotspotID)) {
      warnOnceForAutoTrigger(
        `loop:${hotspotID}`,
        `Hotspot "${hotspotID}" tried to auto-trigger twice with no learner interaction in between, which indicates a dependency loop in its "Auto trigger dependencies". Skipping it until the learner interacts.`
      );
      return false;
    }

    // Deliberately NOT gated on `hotspot.triggered`. Whether completing a
    // hotspot should stop it auto-triggering again is an authoring decision, not
    // the engine's: a scene expresses it by depending on the hotspot's own
    // untriggered state, e.g.
    //   hotspots: [{ id: <this hotspot>, requirement: { triggered: false } }]
    // Because `triggered` is monotonic, that dependency becomes permanently
    // unsatisfied on completion and the hotspot never fires again. Omitting it
    // means "offer this every time the dependencies are met afresh", which a
    // hardcoded rule here would make impossible to express.
    if (checkHotspotLockedDependencies(hotspot)) return false;
    if (!checkHotspotVisibilityDependencies(hotspot)) return false;
    if (!checkHotspotHiddenDependencies(hotspot)) return false;

    // The hotspot must be rendered AND something the learner could have
    // activated themselves. Rendered, because the click action reads the
    // hotspot's own bounding rect and acting on a hotspot nobody can see would
    // be inexplicable. Activatable, because `postTrigger: "inactive"` renders a
    // triggered hotspot disabled — "looks used, no longer usable" — and the
    // engine must not keep performing an action the author has closed off. That
    // case only became reachable once the `triggered` gate above was removed,
    // which used to short-circuit it.
    const element = document.getElementById(`hotspotBtn_${hotspotID}`);
    if (!element || element.disabled || element.tabIndex < 0) return false;

    return true;
  };

  const getAutoTriggerAnnouncement = (hotspotID, hotspot) => {
    const authored = hotspot.autoTriggerAnnouncement;
    if (authored && authored.trim() !== "") {
      // The authored English string doubles as the translation key, as it does
      // for every other authored string in this engine (tooltipText, hotspotAlt,
      // text bodies), which is what makes `?lng=` work.
      //
      // An announcement is free prose, so unlike a tooltip label it may contain
      // ICU-significant characters. Verified that this needs no guard here: given
      // `{n}` or even a malformed `{`, @formatjs logs a FORMAT_ERROR and renders
      // the default message VERBATIM, so the learner still hears the authored
      // sentence. A try/catch around this would be unreachable — formatMessage
      // does not throw. See the brace test in index.spec.js.
      return intl.formatMessage({
        id: authored,
        defaultMessage: authored,
        description: "Announcement for an automatically triggered hotspot",
      });
    }

    // An automatic change of context that says nothing is not acceptable (WCAG
    // 4.1.3 Status Messages), so fall back to a generic message and surface the
    // authoring gap rather than acting silently.
    warnOnceForAutoTrigger(
      `announcement:${hotspotID}`,
      `Hotspot "${hotspotID}" auto-triggers without an "autoTriggerAnnouncement".`
    );
    return intl.formatMessage(
      {
        id: "autoTriggerFallbackAnnouncement",
        defaultMessage: "{label} activated automatically.",
        description:
          "Announcement used when a hotspot is triggered automatically but the scene authored no announcement of its own",
      },
      {
        label:
          hotspot.tooltipText ||
          intl.formatMessage({
            id: "hotspot",
            defaultMessage: "hotspot",
            description:
              "Generic label for a hotspot, used when it has no tooltip text",
          }),
      }
    );
  };

  // Perform a hotspot's normal click action on the user's behalf, reusing the
  // one click handler every manual activation goes through so the two paths
  // cannot drift as hotspot types are added.
  //
  // Returns whether the action opened a widget. That is observed from whether
  // the handler called `updateWidgetData` — a fact about what the action did,
  // not a list of types to keep in sync — and it decides how the announcement
  // reaches the user (dialog name vs live region).
  const dispatchAutoTrigger = (hotspotID, hotspot) => {
    let openedWidget = false;

    createClickHandler({
      id: hotspotID,
      // Automatic activation must never spring the Usersnap feedback widget on
      // someone: asking for feedback is a deliberate user request.
      data: { ...hotspot, showUsersnapFeedback: false },
      changeView,
      changeState,
      handleTrigger,
      updateWidgetData: (active, type, id) => {
        if (active) openedWidget = true;
        updateWidgetData(active, type, id);
      },
    })();

    return openedWidget;
  };

  const checkCompleteDependencies = (id) => {
    const hs = hotspots[id];
    if (!hs) return;

    // get complete_dependencies
    const cd = hs.complete_dependencies || {};
    const {
      views: cdViews = [],
      hotspots: cdHotspots = [],
      customTriggers: cdTriggers = [],
    } = cd;

    // if no complete deps at all, return
    if (
      cdViews.length === 0 &&
      cdHotspots.length === 0 &&
      cdTriggers.length === 0
    ) {
      return;
    }

    // 1) view-based dependency
    if (cdViews.length > 0 && !cdViews.includes(currentView)) {
      return;
    }

    // 2) hotspot-based complete_dependencies
    for (const { id: depID, requirement } of cdHotspots) {
      const target = hotspots[depID];
      if (!target) return;

      for (const prop in requirement) {
        const actual =
          prop === "props" ? target.props.currentState : target[prop];
        const expected =
          prop === "props" ? requirement.props.currentState : requirement[prop];

        if (actual !== expected) {
          return;
        }
      }
    }

    // 3) customTriggers
    if (
      cdTriggers.length > 0 &&
      !cdTriggers.every((name) => isTriggerActive(triggerLog, name))
    ) {
      return;
    }

    // All complete-deps passed - mark it triggered
    setHotspots((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        triggered: true,
      },
    }));
  };

  // Resolve a view's description: its viewAltText, else the alt text of the first
  // active background image entity in that view. "" when neither exists.
  const resolveViewDescription = (viewId) => {
    let description = views[viewId]?.viewAltText || "";
    if (!description) {
      const view = views[viewId];
      for (const entity of entities) {
        if (entity.type !== "image") continue;
        const stateIndex = checkEntityState(entity, viewId);
        if (stateIndex === null) continue;
        // checkEntityState only consults state_dependencies.views, and a state
        // with `views: []` is active in EVERY view — so without the geometry
        // test a single globally-active image would become the description of
        // every view (and, because a described view steals focus to speak it,
        // would move focus in every view too). Apply the same overlap test the
        // renderer uses to decide whether the image is visible here at all.
        const state = entity.states[stateIndex];
        if (view && state) {
          const rect = {
            x: state.x_offset,
            y: state.y_offset,
            w: state.width,
            h: state.height,
          };
          if (!rectOverlapsView(rect, view)) continue;
        }
        const alt = state?.props?.altText;
        if (alt) {
          description = alt;
          break;
        }
      }
    }
    return description;
  };

  // Announce a view on entry. On the FIRST entry to a view we read its
  // description; on any later return we fall back to the generic message.
  // Called synchronously alongside the setCurrentView that enters the view, so
  // everything below commits in the same render as that view change.
  //
  // A first-time description is delivered as the canvas region's accessible
  // name, and the focus effect then lands on that region — so it is spoken as
  // the FOCUS announcement, before any hotspot. The polite live region cannot
  // deliver it first: "polite" means "wait until the user is idle", so it is
  // always queued behind whatever the focus move announced, no matter how early
  // the text is written. The generic message keeps using the live region: it is
  // a status update, not something worth moving focus for.
  //
  // `isInitialEntry` is the page load, where a view with no description must say
  // nothing at all — the generic "View changed" would be untrue when nothing has
  // moved yet.
  const announceViewEntry = (viewId, { isInitialEntry = false } = {}) => {
    const firstEntry = !announcedViewDescriptionsRef.current.has(viewId);
    if (firstEntry) {
      announcedViewDescriptionsRef.current.add(viewId);
    }
    const description = firstEntry ? resolveViewDescription(viewId) : "";

    if (description) {
      setViewRegionLabel(
        intl.formatMessage({
          id: description,
          defaultMessage: description,
          description: "View description",
        })
      );
      pendingRegionFocusRef.current = true;
      return;
    }

    setViewRegionLabel("");
    // Drop any region-focus request left over from an earlier view. Without
    // this the flag can stay latched (the focus effect early-returns while the
    // scene is still loading) and focus would land on a role="region" whose
    // aria-label is now "" — an unnamed focus stop, the very failure the region
    // exists to avoid.
    pendingRegionFocusRef.current = false;
    if (isInitialEntry) {
      return;
    }
    setScreenReaderAnnouncement(
      intl.formatMessage({
        id: "viewNavigationChange",
        defaultMessage: "View changed. New hotspots may be available.",
      })
    );
  };

  // Describe the view the scene opens on, once. Every other view is described by
  // announceViewEntry as it is entered; without this the starting view — the home
  // view — is the one view a screen reader user never hears described, so they
  // land on its first hotspot with no idea what they are looking at.
  const announceStartingView = ({ isInitialEntry = false } = {}) => {
    if (startingViewAnnouncedRef.current) {
      return;
    }
    startingViewAnnouncedRef.current = true;
    announceViewEntry(currentView, { isInitialEntry });
  };

  // Describe the starting view on load. Whether focus is ALSO taken depends on
  // where the canvas lives:
  //  - standalone (no Notebook sidebar): focus the region so the description is
  //    the load announcement and Tab reaches the pause/play control or first
  //    hotspot by native DOM order — the long-standing behaviour.
  //  - embedded in a hotspot sim: the shell owns initial focus (it lands on the
  //    active sidebar section — sidebar-first navigation), so the canvas must
  //    NOT steal it. The home-view region is a tab stop (see its tabIndex), so
  //    the user reaches it — and hears the description — by tabbing in from the
  //    sidebar. Clearing the pending request here also stops a described home
  //    view from leaking a stale region-focus request into the first in-section
  //    view change.
  // With an intro dialog, closeIntroModal announces it instead — the dialog is
  // aria-modal, so anything announced while it is open is hidden from assistive
  // technology and simply lost.
  useEffect(() => {
    if (introModal) {
      return;
    }
    announceStartingView({ isInitialEntry: true });
    if (props.standalone) {
      // Take focus only when there is a description to deliver.
      pendingViewFocusRef.current = pendingRegionFocusRef.current;
    } else {
      pendingRegionFocusRef.current = false;
      pendingViewFocusRef.current = false;
    }
  }, []);

  const changeView = (view, viaHotspotId, viaHotspotData) => {
    // Remember the content view-changer used to leave the current view so
    // focus can return to it when the user navigates back (return-to-trigger,
    // WCAG 2.4.3). Back/Next/Up/Down controls are never recorded: re-entering
    // a view should not land focus on the control that previously left it.
    if (
      viaHotspotId !== undefined &&
      isNavigableContentHotspot(viaHotspotData)
    ) {
      returnFocusHotspotByViewRef.current[currentView] = viaHotspotId;
    }
    const nextView = view === currentView ? props.sceneData.mainView : view;
    setCurrentView(nextView);
    announceViewEntry(nextView);
  };

  const changeState = (id, newIndex) => {
    const newHotspots = { ...hotspots };
    if (typeof newIndex === "number") {
      newHotspots[id].props.currentState = newIndex;
    } else {
      if (newHotspots[id].props.currentState === newHotspots[id].props.states) {
        newHotspots[id].props.currentState = 1;
      } else {
        newHotspots[id].props.currentState++;
      }
    }
    setHotspots({ ...newHotspots });
  };

  const nextHotspotRef = useRef(null);

  // Track the user's last input modality. We only restore focus to a hotspot
  // after a widget closes when the user is navigating by keyboard. Restoring
  // focus for pointer users plants a persistent :focus-visible ring (and the
  // hover/focus tooltip) on the hotspot that lingers after the popup closes and
  // the pointer moves away — the reported "selection box hangs over the question
  // mark". Keyboard users still get focus restored per WCAG 2.4.3 (Focus Order).
  //
  // Note: `pointerdown` also covers touch/pen, so touch screen-reader users
  // (VoiceOver/TalkBack) fall back to <body> on close rather than return-to-
  // trigger. This is an accepted tradeoff: a scripted focus() still registers as
  // :focus-visible on touch, so returning focus there would reintroduce the
  // stuck ring for the far more common touch case. This mirrors native
  // :focus-visible semantics (touch/mouse suppress it, keyboard shows it).
  const lastInputWasKeyboardRef = useRef(false);
  useEffect(() => {
    const onKeyDown = () => {
      lastInputWasKeyboardRef.current = true;
    };
    const onPointerDown = () => {
      lastInputWasKeyboardRef.current = false;
    };
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, []);

  const handleTriggerContentBox = (id) => {
    if (props.requiresRedux && dispatch) {
      const newHotspots = props.sceneData.hotspots;
      if (newHotspots[id].isCorrect === false) {
        dispatch(
          isqUnselectOption({
            hotspotId: id,
            componentID: props.componentID,
          })
        );
      }
    } else {
      console.warn("Redux is not enabled. Skipping dispatch.");
    }
  };

  const forceStateChangers = (stateChangers) => {
    if (!Array.isArray(stateChangers) || stateChangers.length === 0) return;
    const newHotspots = { ...hotspots };
    stateChangers.forEach(({ id, state }) => {
      if (newHotspots[id]?.props?.type === "state-changer") {
        newHotspots[id] = {
          ...newHotspots[id],
          props: {
            ...newHotspots[id].props,
            currentState: state,
          },
        };
        setHotspots(newHotspots);
      }
    });
  };

  const handleTrigger = (id) => {
    if (hotspots[id].preventSelfCompletion === true) {
      return; // This hotspot is only completed through dependencies
    }

    // Create a deep copy of the hotspots state
    const newHotspots = { ...hotspots };

    // Record which hotspot was triggered for progress logging
    setLastTriggeredHotspot({
      id: id,
      tooltip:
        newHotspots[id].tooltipText ||
        newHotspots[id].description ||
        newHotspots[id].props?.tooltip ||
        "",
      type: newHotspots[id].props?.type || "unknown",
    });

    if (newHotspots[id].props.type === "content-box") {
      handleTriggerContentBox(id);
    } else {
      newHotspots[id] = {
        ...newHotspots[id],
        triggered: true, // Ensure immutability
      };
      setHotspots(newHotspots);
    }

    // If this trigger completes the section AND kicks off a delayed image reveal
    // (e.g. Take photo -> flash animation -> "Snapshot taken!"), mark a reveal
    // pending so trackProgress HOLDS the completion report until the reveal is
    // announced. Set synchronously here, before the commit, so the held state is
    // in place when trackProgress runs in the post-commit effect.
    if (props.trackProgress && !pendingRevealTriggerRef.current) {
      const revealTrigger = sectionRevealTrigger(entities, triggerLog, id);
      if (revealTrigger) {
        pendingRevealTriggerRef.current = revealTrigger;
        // Safety net: if the reveal never announces, release the held report so
        // the section can never stall as incomplete.
        revealWatchdogRef.current = setTimeout(flushHeldProgress, 6000);
      }
    }

    if (newHotspots[id].postTrigger === "hidden" && hotspotOrder) {
      setTimeout(() => {
        const currentIndex = hotspotOrder.indexOf(id);
        if (currentIndex !== -1 && currentIndex < hotspotOrder.length - 1) {
          const nextHotspotId = hotspotOrder[currentIndex + 1];
          const nextHotspotElement = document.getElementById(
            `hotspotBtn_${nextHotspotId}`
          );
          if (nextHotspotElement) {
            nextHotspotRef.current = nextHotspotElement;
          } else {
            nextHotspotRef.current = null;
          }
        }
      }, 300);
    }
  };

  const updateWidgetData = (bool, type, hotspotID) => {
    // Default every widget to "not automatically opened"; the auto-trigger path
    // re-sets this straight after calling through.
    setAutoOpenedWidgetAnnouncement("");

    const newWidgetData = { ...widgetData };
    newWidgetData.type = type;
    newWidgetData.hotspotID = hotspotID;
    newWidgetData.active = bool;

    setWidgetData({ ...newWidgetData });
  };

  // State a question has reported but that the scene has not taken up yet, keyed
  // by hotspot. Held rather than applied so the scene changes as the widget
  // closes instead of underneath a learner who is still reading their feedback.
  const pendingQuestionStateRef = useRef({});

  /**
   * Records the state a question's submitted option maps to. The scene picks it
   * up when the widget closes (see applyPendingQuestionState), so entities and
   * other hotspots can depend on the answer through the existing
   * `props.currentState` checks.
   *
   * Every submission is recorded, not only the one that completes the question:
   * a wrong answer on a question with attempts left still counts, and whichever
   * submission the learner leaves the widget on is the one the scene takes.
   */
  const handleQuestionState = React.useCallback((hotspotID, state) => {
    pendingQuestionStateRef.current[hotspotID] = state;
  }, []);

  const applyPendingQuestionState = (hotspotID) => {
    const pendingState = pendingQuestionStateRef.current[hotspotID];
    if (pendingState === undefined) return;

    delete pendingQuestionStateRef.current[hotspotID];

    setHotspots((prev) => {
      const hotspot = prev[hotspotID];
      if (!hotspot || hotspot.props.currentState === pendingState) {
        return prev; // Unchanged: don't churn dependents or re-persist the scene
      }

      return {
        ...prev,
        [hotspotID]: {
          ...hotspot,
          props: {
            ...hotspot.props,
            currentState: pendingState,
          },
        },
      };
    });
  };

  const handleCloseWidget = () => {
    // Let the scene take up the answer as the widget closes, so it has settled
    // by the time the learner can see it rather than shifting behind the modal.
    applyPendingQuestionState(widgetData.hotspotID);

    setCloseWidget(true);
    setTimeout(() => {
      setCloseWidget(false);
      updateWidgetData(false, "none", 0);

      // Only pull focus back onto a hotspot for keyboard users. For pointer
      // users this would leave a stuck focus ring/tooltip over the hotspot.
      if (lastInputWasKeyboardRef.current) {
        if (nextHotspotRef?.current) {
          nextHotspotRef.current.focus();
        }

        if (latestHotspotRef?.current) {
          latestHotspotRef.current.focus();
        }
      }
    }, 300);
  };

  const handleWidgetComplete = (hotspotID) => {
    const newHotspots = { ...hotspots };
    const widget = newHotspots[hotspotID].props;
    if (!widget.complete) {
      widget.complete = true;
      setHotspots({ ...newHotspots });
    }
  };

  const handleWidgetReset = (hotspotID) => {
    const newHotspots = { ...hotspots };
    const widget = newHotspots[hotspotID].props;

    newHotspots[hotspotID] = {
      ...newHotspots[hotspotID],
      triggered: false,
      props: {
        ...widget,
        complete: false,
      },
    };
    setHotspots(newHotspots);
  };

  const fireTrigger = (triggerName, selfAnnounced = false) => {
    // fireTrigger is only ever called by AnimationEntity on completion. Normally
    // a control revealed by this trigger is "newly available because an
    // animation finished" (e.g. the timer ending) — worth announcing via the
    // effect below. But when the animation announces its OWN completion status
    // (selfAnnounced — e.g. the timer's "The timer has ended…"), that message
    // supersedes the generic revealed-control one, so we skip recording the
    // pending trigger to avoid a duplicate/confusing announcement.
    if (!selfAnnounced) {
      pendingAnimationTriggerRef.current = triggerName;
    }
    const updatedTriggerLog = triggerLog;
    updatedTriggerLog.push(triggerName);
    setTriggerLog([...updatedTriggerLog]);
  };

  // Announce an animation's start/complete status (delegated from AnimationEntity,
  // which is aria-hidden) via the secondary live region — the same region the
  // revealed-control announcer uses, so animation-status messages never collide
  // with a text-body/alt update in the primary region.
  const handleAnimationAnnounce = React.useCallback(
    (message) => {
      if (!message) return;
      announceSecondary(
        intl.formatMessage({
          id: message,
          defaultMessage: message,
          description: "Animation status announcement",
        })
      );
    },
    [intl, announceSecondary]
  );

  const undoTrigger = (triggerName) => {
    if (triggerLog.includes(triggerName)) {
      let updatedTriggerLog = triggerLog;
      updatedTriggerLog = updatedTriggerLog.filter((e) => e !== triggerName);
      setTriggerLog([...updatedTriggerLog]);
    }
  };

  const canvasStyle = CSS.supports
    ? CSS.supports("aspect-ratio: 1/1")
      ? {
          aspectRatio: `${views[currentView].size.width}/${views[currentView].size.height}`,
        }
      : {
          // fallback for no aspect-ratio support...
          width:
            window.innerWidth / window.innerHeight <
            views[currentView].size.width / views[currentView].size.height
              ? // portrait (relative to view): fill width, compute height from vw
                "100vw"
              : // landscape (relative to view): compute width from vh
                `${(views[currentView].size.width / views[currentView].size.height) * 100}vh`,
          height:
            window.innerWidth / window.innerHeight <
            views[currentView].size.width / views[currentView].size.height
              ? // portrait (relative to view): compute height from vw
                `${(views[currentView].size.height / views[currentView].size.width) * 100}vw`
              : // landscape (relative to view): fill height
                "100vh",
        }
    : {
        // if CSS.supports isn’t available, assume fallback
        width:
          window.innerWidth / window.innerHeight <
          views[currentView].size.width / views[currentView].size.height
            ? "100vw"
            : `${(views[currentView].size.width / views[currentView].size.height) * 100}vh`,
        height:
          window.innerWidth / window.innerHeight <
          views[currentView].size.width / views[currentView].size.height
            ? `${(views[currentView].size.height / views[currentView].size.width) * 100}vw`
            : "100vh",
      };

  return (
    <div
      ref={canvasContainerRef}
      className={className.canvasContainer}
      role="presentation"
      allow="fullscreen"
      allowFullScreen
      id={`canvas_${props.sceneData.id}`}
      style={{
        ...canvasStyle,
        aspectRatio: "auto",
      }}
    >
      <div
        ref={containerRef}
        className={className.canvas}
        style={{
          ...canvasStyle,
          overflow: devView ? "scroll" : "clip",
          maxHeight: props.standalone ? "100vh" : "100%",
        }}
        role="region"
        // A tab stop once loaded and carrying a description ("home-view" in the
        // requested order home-view -> pause/play -> first interactable): tabbing
        // in from the sidebar lands here and the aria-label (the view
        // description) is read as the focus announcement, ahead of pause/play and
        // any hotspot. Falls back to -1 (programmatically focusable only, never a
        // Tab stop) while still loading or when the view has no description, so an
        // unnamed region is never a focus stop. The view-transition effect can
        // still focus it programmatically at -1 (return-to-home, described view
        // changes).
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- intentional: a labeled region deliberately made a focusable Tab stop so its description is read as the focus announcement
        tabIndex={loaded && viewRegionLabel ? 0 : -1}
        // The description deliberately takes precedence over the
        // "All hotspots have been navigated" name, and is NOT combined with it.
        // `view.complete` is not a trustworthy source for a region name: it is
        // already true on a fresh load in scenes whose hotspots have not been
        // touched (it is set from `triggeredActions === maxActions`, which holds
        // vacuously at 0 === 0), so surfacing it here would tell the user they
        // had finished a view they had not started. Exposing completion properly
        // requires fixing how it is computed, and belongs with the announcer
        // rework where it can be sequenced rather than raced against the
        // description this region exists to deliver.
        aria-label={
          !loaded
            ? FormattedMessage({
                string: "Loading",
              })
            : viewRegionLabel ||
              (views[currentView].complete
                ? intl.formatMessage({
                    id: "allHotspotsNavigated",
                    defaultMessage: "All hotspots have been navigated",
                  })
                : "")
        }
        // No Tab handler by design. The region is only a focus stop, so native
        // sequential focus navigation carries the user onward: the pause/play
        // control is the region's first child when the scene has looping
        // animations, otherwise the first hotspot in `hotspotOrder` is. A screen
        // reader's virtual-cursor keys are intercepted by the screen reader and
        // never reach the page, so DOM order is the only order that can serve
        // both — intercepting Tab here would make them disagree.
      >
        {hasLoopingAnimations && (
          <div className={className.animationControlWrapper}>
            <Hotspot
              id="animation-control"
              data={{
                props: {
                  type: "animation-control",
                },
                triggered: false,
                postTrigger: "no-change",
                visualOverride: pauseLoopingAnimations
                  ? "Animation-Play"
                  : "Animation-Pause",
                tooltipText: pauseLoopingAnimations
                  ? "Play all decorative animations"
                  : "Pause all decorative animations",
                visibility_dependencies: {
                  views: [],
                },
              }}
              setLatestHotspotRef={noop}
              changeView={noop}
              changeState={noop}
              handleTrigger={() => {
                setPauseLoopingAnimations(!pauseLoopingAnimations);
              }}
              updateWidgetData={noop}
              locked={false}
              hide={false}
              showGrid={false}
              currentView={currentView}
              idle={false}
              device={device}
              cellSize={cellSize}
              viewData={views[currentView]}
              transitionSpeed={0}
              isDiabledBlurOnSmall={false}
            />
          </div>
        )}
        {!loaded && props.animationCache ? (
          <div
            className={className.canvasSpinner}
            data-testid="canvas-spinner"
            role="status"
            aria-live="polite"
          >
            <div className={className.canvasSpinnerCircle} aria-hidden="true" />
            <span className={className.visuallyHidden}>
              {FormattedMessage({ string: "Loading" })}
            </span>
          </div>
        ) : null}
        {loaded || props.animationCache ? (
          <div
            className={className.gridContainer}
            role="presentation"
            style={{
              width: `calc(${(maxViewX / views[currentView].size.width) * 100 * Object.keys(views).length}%)`,
              height: `100%`,
              gridTemplateColumns: `repeat(${gridSize.x}, ${cellSize}px)`,
              gridTemplateRows: `repeat(${views[currentView].size.height}, ${cellSize}px)`,
              transform: devView
                ? null
                : `translate3d(
            -${(views[currentView].x_offset - 1) * cellSize}px,
            -${(views[currentView].y_offset - 1) * cellSize}px,
             0
          )`,
              willChange: "transform",
              transition: `${props.sceneData.transitionSpeed}s`,
              // When animationCache is supplied, the grid is mounted before
              // `loaded` flips so AnimationEntity instances can fetch and emit
              // 'load'. Hide the grid visually until the gate opens.
              opacity: loaded ? 1 : 0,
              pointerEvents: loaded ? "auto" : "none",
            }}
            aria-hidden={!loaded}
          >
            {entities.map((entity, key) => {
              const state = checkEntityState(entity);

              if (state !== null) {
                if (entity.type === "image") {
                  return (
                    <GridItem
                      key={key}
                      type="entity"
                      gridColumnStart={entity.states[state].x_offset}
                      gridColumnEnd={
                        entity.states[state].x_offset +
                        entity.states[state].width
                      }
                      gridRowStart={entity.states[state].y_offset}
                      gridRowEnd={
                        entity.states[state].y_offset +
                        entity.states[state].height
                      }
                      show={false}
                      dev={dev && showGrid}
                      viewData={views[currentView]}
                    >
                      {preview ||
                      resources[
                        `${entity.states[state].props.src}_${intl.locale}`
                      ] !== undefined ||
                      resources[`${entity.states[state].props.src}`] !==
                        undefined
                        ? (() => {
                            const view = views[currentView];
                            const rect = {
                              x: entity.states[state].x_offset,
                              y: entity.states[state].y_offset,
                              w: entity.states[state].width,
                              h: entity.states[state].height,
                            };

                            const inView = rectOverlapsView(rect, view);
                            const hideFromAT = !inView && !devView;

                            return (
                              <div {...getA11yHiddenProps(hideFromAT)}>
                                <ImageEntity
                                  state={state}
                                  data={entity.states[state]}
                                  src={
                                    resources[
                                      `${entity.states[state].props.src}_${intl.locale}`
                                    ] ||
                                    resources[
                                      `${entity.states[state].props.src}`
                                    ]
                                  }
                                  allowOverflow={
                                    entity.states[state].props.allowOverflow
                                  }
                                  preview={preview}
                                  viewAltText={views[currentView].viewAltText}
                                />
                              </div>
                            );
                          })()
                        : null}
                    </GridItem>
                  );
                } else if (entity.type === "notebook") {
                  return (
                    <GridItem
                      key={key}
                      type="entity"
                      gridColumnStart={entity.states[state].x_offset}
                      gridColumnEnd={
                        entity.states[state].x_offset +
                        entity.states[state].width
                      }
                      gridRowStart={entity.states[state].y_offset}
                      gridRowEnd={
                        entity.states[state].y_offset +
                        entity.states[state].height
                      }
                      show={true}
                      dev={dev && showGrid}
                      viewData={views[currentView]}
                    >
                      <NotebookEntity
                        state={state}
                        data={entity.states[state]}
                        allowOverflow={entity.states[state].props.allowOverflow}
                        preview={preview}
                      />
                    </GridItem>
                  );
                } else return null;
              } else return null;
            })}
            {entities.map((entity, key) => {
              const state = checkEntityState(entity);

              if (entity.type === "animation") {
                const animId = entity.id || `anim-${key}`;
                const colorKey = `${animId}-${state}`;
                const isHidden = hiddenAnimations.has(colorKey);
                const isContentHidden = hiddenAnimationContent.has(colorKey);
                const isHighlighted =
                  showAnimationHighlight &&
                  state !== null &&
                  !isHidden &&
                  !isContentHidden;
                const highlightColor = isHighlighted
                  ? animationColorMap.get(colorKey)
                  : null;

                return (
                  <GridItem
                    key={key}
                    type="entity"
                    gridColumnStart={entity.x_offset}
                    gridColumnEnd={entity.x_offset + entity.width}
                    gridRowStart={entity.y_offset}
                    gridRowEnd={entity.y_offset + entity.height}
                    show={false}
                    dev={dev && showGrid}
                    viewData={views[currentView]}
                    style={
                      isHighlighted
                        ? {
                            border: `3px solid ${highlightColor}`,
                            boxShadow: `0 0 15px ${highlightColor}`,
                            position: "relative",
                          }
                        : {}
                    }
                  >
                    {isHighlighted && (
                      <>
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor:
                              highlightColor || "rgba(255, 0, 0, 0.1)",
                            opacity: 0.1,
                            pointerEvents: "none",
                            zIndex: 9999,
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "4px",
                            left: "4px",
                            backgroundColor: highlightColor || "#999",
                            color: "#fff",
                            padding: "2px 6px",
                            fontSize: "11px",
                            fontWeight: "bold",
                            borderRadius: "3px",
                            pointerEvents: "none",
                            zIndex: 10000,
                            fontFamily: "sans-serif",
                          }}
                        >
                          Anim {animId}
                        </div>
                      </>
                    )}
                    <div style={{ opacity: isContentHidden ? 0 : 1 }}>
                      {(() => {
                        const view = views[currentView];
                        const rect = {
                          x: entity.x_offset,
                          y: entity.y_offset,
                          w: entity.width,
                          h: entity.height,
                        };

                        const inView = rectOverlapsView(rect, view);
                        const hideFromAT = !inView && !devView;

                        return (
                          <div
                            {...getA11yHiddenProps(hideFromAT)}
                            style={{ opacity: isContentHidden ? 0 : 1 }}
                          >
                            <AnimationEntity
                              hidden={state === null}
                              state={state || entity.states[0]}
                              data={entity.states[state] || entity.states[0]}
                              src={entity.props.src}
                              fireTrigger={fireTrigger}
                              undoTrigger={undoTrigger}
                              onAnnounce={handleAnimationAnnounce}
                              previewSrc={entity.props.url}
                              allowOverflow={entity.props.allowOverflow}
                              playedStates={playedStates.current}
                              onStateMarkedPlayed={handleStateMarkedPlayed}
                              preview={preview}
                              pauseLooping={pauseLoopingAnimations}
                              animationData={
                                props.animationCache?.get(
                                  `${baseUrl}locales/${intl.locale}/animations/${entity.props.src ?? ""}`
                                ) ?? undefined
                              }
                              onReady={
                                props.animationCache
                                  ? handleAnimationReady
                                  : undefined
                              }
                            />
                          </div>
                        );
                      })()}
                    </div>
                  </GridItem>
                );
              } else return null;
            })}
            {showGrid ? (
              <div
                className={className.gridLines}
                style={{
                  gridTemplateColumns: `repeat(${gridSize.x}, ${cellSize}px)`,
                  gridTemplateRows: `repeat(${views[currentView].size.height}, ${cellSize}px)`,
                }}
              >
                {grid.map((cell, key) => {
                  let redundant = false;
                  Object.keys(hotspots).map((hotspotID) => {
                    const hotspot = hotspots[hotspotID];
                    if (
                      checkHotspotVisibilityDependencies(hotspot) &&
                      checkHotspotHiddenDependencies(hotspot, hotspotID)
                    ) {
                      if (
                        cell.y >= hotspot.y_offset &&
                        cell.y <= hotspot.y_offset + hotspot.height - 1
                      ) {
                        if (
                          cell.x >= hotspot.x_offset &&
                          cell.x <= hotspot.x_offset + hotspot.width - 1
                        ) {
                          redundant = true;
                        }
                      }
                    }
                  });
                  return (
                    <GridItem
                      key={key}
                      type={redundant && showHotspots ? "hidden" : "cell"}
                      gridColumnStart={cell.x}
                      gridColumnEnd={cell.x + 1}
                      gridRowStart={cell.y}
                      gridRowEnd={cell.y + 1}
                      show={showGrid}
                      dev={dev && showGrid}
                      viewData={views[currentView]}
                    ></GridItem>
                  );
                })}
              </div>
            ) : null}
            {hotspotOrder.map((hotspotID) => {
              const hotspot = hotspots[hotspotID];
              if (!hotspot) return null; // Prevent errors if a hotspot is missing

              if (!hotspot.triggered) {
                checkCompleteDependencies(hotspotID);
              }
              const visible =
                checkHotspotVisibilityDependencies(hotspot) &&
                checkHotspotHiddenDependencies(hotspot);
              const locked = checkHotspotLockedDependencies(hotspot);

              const isCorrect = props.sceneData.hotspots[hotspotID]?.isCorrect;
              if (props.sceneData.hotspots[hotspotID].props.image) {
                hotspot.props.image =
                  props.sceneData.hotspots[hotspotID].props.image;
              }

              const triggersAnimation = willTriggerAnimation(
                hotspotID,
                hotspot,
                props.sceneData.entities,
                currentView,
                hotspots,
                triggerLog
              );

              const animationAltTexts = getAnimationAltTexts(
                hotspotID,
                hotspot,
                props.sceneData.entities,
                currentView,
                hotspots,
                triggerLog
              );

              if (visible) {
                return (
                  <GridItem
                    key={hotspotID}
                    type="hotspot"
                    gridColumnStart={hotspot.x_offset}
                    gridColumnEnd={hotspot.x_offset + hotspot.width}
                    gridRowStart={hotspot.y_offset}
                    gridRowEnd={hotspot.y_offset + hotspot.height}
                    show={showGrid && showHotspots}
                    dev={dev && showGrid}
                    viewData={views[currentView]}
                  >
                    <Hotspot
                      id={hotspotID}
                      selectedHotspot={props.selectedHotspot}
                      setLatestHotspotRef={(ref) => setLatestHotspotRef(ref)}
                      data={hotspot}
                      changeView={changeView}
                      changeState={changeState}
                      handleTrigger={handleTrigger}
                      updateWidgetData={updateWidgetData}
                      locked={locked}
                      hide={!showHotspots}
                      showGrid={showGrid}
                      currentView={currentView}
                      idle={
                        !views[currentView].complete &&
                        tick >= 2 &&
                        tick % 2 === 0
                      }
                      device={device}
                      cellSize={cellSize}
                      viewData={views[currentView]}
                      transitionSpeed={props.sceneData.transitionSpeed}
                      isDiabledBlurOnSmall={props.isDiabledBlurOnSmall}
                      isCorrect={isCorrect}
                      usersnapProjectId={props.sceneData.usersnap}
                      triggersAnimation={triggersAnimation}
                      animationAltTexts={animationAltTexts}
                      hotspotOrder={hotspotOrder}
                      allHotspots={hotspots}
                    />
                  </GridItem>
                );
              }
              return null;
            })}
          </div>
        ) : (
          <div className={className["fallback-loading"]} aria-hidden={true}>
            <div>.</div>
            <div>.</div>
            <div>.</div>
          </div>
        )}
      </div>
      {views[currentView].backToView !== null && (
        <div
          className={`${className.viewBackBtn} ${currentView === props.sceneData.mainView ? className.hidden : ""}`}
        >
          {isNarrowViewport ? (
            <button
              className={className.backHotspot}
              tabIndex={0}
              onClick={() => {
                const targetView = views[currentView].backToView;
                setCurrentView(targetView);
                announceViewEntry(targetView);
              }}
              aria-label={intl.formatMessage({
                id: "backToPreviousView",
                defaultMessage: "Back to previous view",
              })}
            >
              ←
            </button>
          ) : (
            <LxButton
              variant="oncolor"
              size="medium"
              tabIndex={0}
              onClickHandler={() => {
                const targetView = views[currentView].backToView;
                setCurrentView(targetView);
                announceViewEntry(targetView);
              }}
              text="Back"
              trailingIcon="back"
            />
          )}
        </div>
      )}
      {widgetData.active || closeWidget ? (
        <WidgetWrapper
          type={widgetData.type}
          hotspotID={widgetData.hotspotID}
          handleTrigger={handleTrigger}
          closeWidget={closeWidget}
          widgetProps={hotspots[widgetData.hotspotID].props}
          handleCloseWidget={handleCloseWidget}
          handleWidgetComplete={handleWidgetComplete}
          handleWidgetReset={handleWidgetReset}
          handleQuestionState={handleQuestionState}
          preview={preview}
          autoTriggerAnnouncement={autoOpenedWidgetAnnouncement}
        />
      ) : null}
      {dev && preview && (
        <DevMenu
          showGrid={showGrid}
          setShowGrid={setShowGrid}
          devView={devView}
          setDevView={setDevView}
          showAnimationHighlight={showAnimationHighlight}
          setShowAnimationHighlight={setShowAnimationHighlight}
        />
      )}
      {dev && preview && showAnimationHighlight && (
        <AnimationLegend
          activeAnimations={activeAnimations}
          colorMap={animationColorMap}
          hiddenAnimations={hiddenAnimations}
          hiddenAnimationContent={hiddenAnimationContent}
          onToggleHighlight={toggleAnimationHighlight}
          onToggleContent={toggleAnimationContent}
        />
      )}
      {introModal && (
        <IntroModal
          {...(typeof props.sceneData.intro === "object"
            ? props.sceneData.intro
            : { text: props.sceneData.intro })}
          closeIntroModal={() => {
            // The intro modal opens on mount, so there is no trigger element
            // for focus to return to on close — without this request focus
            // falls to document.body (the surrounding frame). Reuse the
            // view-transition restoration effect to land on the first
            // unvisited interactive hotspot.
            pendingViewFocusRef.current = true;
            // Dismissing the dialog is the user's real entry into the starting
            // view, and the first moment anything outside it is visible to
            // assistive technology. Focus is moving anyway, so the description
            // rides on it (see announceViewEntry) and is heard before the first
            // hotspot.
            //
            // isInitialEntry marks this as an arrival, not a navigation: closing
            // a dialog is not a view change, so the generic "View changed. New
            // hotspots may be available." fallback must not fire when the
            // starting view has no description. The description path itself
            // returns before that flag is consulted, so it is unaffected.
            announceStartingView({ isInitialEntry: true });
            setIntroModal(false);
          }}
        />
      )}
      {showOrientationPrompt && (
        <RotateOverlay
          setOrientationPromptDismissed={setOrientationPromptDismissed}
          setShowOrientationPrompt={setShowOrientationPrompt}
        />
      )}
      {/* Consolidated aria-live region for all screen reader announcements */}
      {/* Handles: view navigation, text-body instructions, image alt text changes */}
      {/* Animation alt text is announced proactively via hotspot aria-labels */}
      {/* Polite (not assertive): these are routine view/alt-text updates, so they
          must queue behind — not interrupt — whatever the user is reading, e.g.
          the hotspot they just focused. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {screenReaderAnnouncement}
      </div>
      {/* Second polite live region for the label of a control an animation
          reveals on completion, so it is announced alongside (not on top of) a
          same-tick instruction/alt update. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {secondaryAnnouncement}
      </div>
      {/* Dedicated live region for auto-triggered actions that did NOT open a
          widget (a state change, an animation, an announce-only hotspot).
          Separate from the consolidated region above so that region's other
          producers — which overwrite it on 1s and 2s timers — cannot clobber an
          automatic action's announcement. Widget-opening auto-triggers announce
          inside their own dialog instead, so nothing is said twice.

          Polite, like the routine region above: an automatic action is a status
          message (WCAG 4.1.3), so it should queue behind whatever the learner is
          hearing rather than cut it off mid-word.

          What makes a REPEAT audible is the `key` — see announceAutoTrigger —
          not the politeness. Established by testing with VoiceOver: polite with
          a stable node dropped identical repeats, assertive with a stable node
          ALSO dropped them, and polite with a fresh node works (as the in-dialog
          announcement, which is polite, already did). Politeness was never the
          differentiator, so do not reach for assertive if repeats regress —
          check that the node is still being replaced. */}
      <div
        key={autoTriggerAnnouncementKey}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={className.visuallyHidden}
        data-testid="auto-trigger-announcement"
      >
        {autoTriggerAnnouncement}
      </div>
    </div>
  );
}

ImageDiscoveryCanvas.propTypes = {
  sceneData: PropTypes.shape({
    mainView: PropTypes.number.isRequired,
    views: PropTypes.object.isRequired,
    hotspots: PropTypes.object.isRequired,
    entities: PropTypes.array.isRequired,
    transitionSpeed: PropTypes.number.isRequired,
  }).isRequired,
  standalone: PropTypes.bool,
  updateProgress: PropTypes.func,
  trackProgress: PropTypes.bool,
  isDiabledBlurOnSmall: PropTypes.bool,
  selectedHotspot: PropTypes.number,
  componentID: PropTypes.number,
  requiresRedux: PropTypes.bool,
  preview: PropTypes.bool,
  initialPlayedStates: PropTypes.arrayOf(PropTypes.number),
  onPlayedStatesUpdate: PropTypes.func,
  // Optional preload hooks: when `animationCache` is supplied, AnimationEntity
  // instances receive `animationData` from the cache and the `loaded` gate
  // waits for every animation's lottie 'load' event before flipping true.
  animationCache: PropTypes.shape({
    get: PropTypes.func.isRequired,
    isPending: PropTypes.func,
  }),
  onAllAnimationsLoaded: PropTypes.func,
};

ImageDiscoveryCanvas.defaultProps = {
  standalone: false,
  trackProgress: false,
  isDiabledBlurOnSmall: false,
  selectedHotspot: null,
  componentID: null,
  requiresRedux: false,
};
